'use client';

import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  alpha,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Bookmark as BookmarkIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import AddressAutocomplete from '@/components/AddressAutocomplete/AddressAutocomplete';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import useApiCall from '@/services/api/useApiCall';
import { useTranslation } from '@/kit/useTranslation';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

export default function PrimaryPickupSelector({ value, onChange }) {
  const { t } = useTranslation();
  const { makeApiCall } = useApiCall();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    const fetchSavedAddresses = async () => {
      try {
        const config = buildApiConfig(endPointEnums.GET_SAVED_ADDRESSES);
        const response = await makeApiCall(config);
        if (response?.success) {
          const pickupAddresses = (response.data || []).filter((addr) => {
            const type = addr.savedAddressType || addr.addressType || '';
            return type === 'PICKUP' || type === 'BOTH';
          });
          setSavedAddresses(pickupAddresses);
        }
      } catch (err) {
        console.error('[PrimaryPickupSelector] Failed to fetch saved addresses:', err);
      } finally {
        setLoadingSaved(false);
      }
    };
    fetchSavedAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    return address.fullAddress || address.address || address.formatted_address || '';
  };

  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    setInputValue(formatAddress(value?.address));
  }, [value]);

  const handlePlaceSelected = useCallback(
    (place) => {
      if (!place) {
        setInputValue('');
        onChange(null);
        return;
      }
      const formatted = place.fullAddress || place.formatted_address || '';
      onChange({
        address: formatted,
        lat: place.latitude || place.lat,
        lng: place.longitude || place.lng,
      });
      setInputValue(formatted);
    },
    [onChange]
  );

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert(t('bulkBooking.geolocationNotSupported'));
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onChange({
          address: `Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          lat: latitude,
          lng: longitude,
        });
        setLocationLoading(false);
      },
      (err) => {
        console.error(err);
        setLocationLoading(false);
        alert(t('bulkBooking.unableToFetchLocation'));
      }
    );
  }, [onChange]);

  const handleQuickSelect = useCallback(
    (addr) => {
      onChange({
        address: addr.savedAddressFullAddress || addr.fullAddress || addr.address,
        lat: addr.savedAddressLatitude || addr.latitude || addr.lat,
        lng: addr.savedAddressLongitude || addr.longitude || addr.lng,
      });
    },
    [onChange]
  );

  const handleClear = useCallback(() => onChange(null), [onChange]);

  return (
    <Box>
      {/* Search Bar with Internal Location Button */}
      <Box sx={{ mb: 2 }}>
        <AddressAutocomplete
          value={inputValue || ''}
          onChange={(v) => setInputValue(v)}
          onPlaceSelected={handlePlaceSelected}
          onUseCurrentLocation={handleUseCurrentLocation}
          label=""
          placeholder={t('bulkBooking.searchPickupAddress')}
          required
          clearable
          size="medium"
          fullWidth
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              padding: '14px 16px',
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#fff',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              },
            },
          }}
        />
      </Box>

      {/* Selected Location Display */}
      {value && value.lat && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '2px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: 4,
              height: '100%',
              bgcolor: '#10b981',
            },
          }}
        >
          <Box
            sx={{
              bgcolor: '#10b981',
              width: 44,
              height: 44,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
            }}
          >
            <LocationIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#10b981',
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
              }}
            >
              {t('bulkBooking.selectedPickup')}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#111827',
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              {formatAddress(value.address)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClear}
            aria-label="clear pickup"
            sx={{
              bgcolor: alpha('#ef4444', 0.1),
              '&:hover': {
                bgcolor: '#ef4444',
                '& svg': { color: '#fff' },
              },
              transition: 'all 200ms',
            }}
          >
            <ClearIcon sx={{ fontSize: 18, color: '#ef4444' }} />
          </IconButton>
        </Box>
      )}

      {/* Saved Addresses */}
      {!loadingSaved ? (
        savedAddresses.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              bgcolor: '#fafafa',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#64748b',
                fontWeight: 800,
                mb: 1.5,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
              }}
            >
              {t('bulkBooking.savedPickups')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {savedAddresses.slice(0, 12).map((addr) => (
                <Chip
                  key={addr.savedAddressId || addr.addressId}
                  label={
                    addr.savedAddressLabel ||
                    addr.addressLabel ||
                    (addr.fullAddress || addr.savedAddressFullAddress || '').substring(0, 30) +
                      '...'
                  }
                  size="medium"
                  onClick={() => handleQuickSelect(addr)}
                  icon={<BookmarkIcon />}
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    '&:hover': {
                      bgcolor: '#f59e0b',
                      color: '#fff',
                      borderColor: '#f59e0b',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(245,158,11,0.25)',
                      '& .MuiChip-icon': { color: '#fff' },
                    },
                    transition: 'all 200ms',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Stack>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px dashed #cbd5e1',
              bgcolor: '#fafafa',
              textAlign: 'center',
            }}
          >
            <BookmarkIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>
              {t('bulkBooking.noSavedPickupsYet')}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
              {t('bulkBooking.searchAndSavePickups')}
            </Typography>
          </Paper>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px dashed #e5e7eb',
            bgcolor: '#fafafa',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={24} sx={{ color: '#f59e0b', mb: 1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            {t('bulkBooking.loadingSavedAddresses')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
